import { ILineItem, OnUpdateLine } from '@features/purchasing/my-basket/types';
import Image from '@store/components/Image';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface IProps extends ILineItem {
  onUpdate: OnUpdateLine;
}

export default function Line(props: IProps) {
  const { t } = useTranslation();

  const [unitCount, setUnitCount] = useState(props.unitCount);

  const onUpdate = useCallback(
    (unitCount: number) => {
      const { productId, optionId, onUpdate } = props;

      onUpdate({ productId, optionId, unitCount });
    },
    [props]
  );

  return (
    <div className="basket-line-row">
      <div className="image">
        <a href={props.productUrl}>
          <Image
            src={props.imageUrl}
            alt={props.productName}
            className="max-h-full object-scale-down"
          />
        </a>
      </div>

      <div className="product">
        <a href={props.productUrl} className="block font-medium leading-normal">
          <span className="font-bold">{props.productName}</span>
          {props.hasDeposit && (
            <span className="icon-deposit ml-1">
              <svg
                aria-hidden="true"
                focusable="false"
                role="img"
                viewBox="43.11 43.11 234.71 196"
                xmlns="http://www.w3.org/2000/svg">
                <path
                  fill="currentColor"
                  d="M277.79 163.814a.793.793 0 0 1-.24.797l-40.65 31.904a.796.796 0 0 1-.675.181.793.793 0 0 1-.555-.426l-19.424-43.805a.788.788 0 0 1 .861-1.123l21.206 4.201a.049.049 0 0 1 .007.003c1.269-6.377 8.942-51.204-19.084-80.759C170.704 23.604 106.954 59.65 106.954 59.65c15.118-15.064 81.406-31.659 121.952 8.648 37.291 37.07 28.66 84.249 27.243 90.777l21.032 4.167c.294.061.53.281.609.572M92.017 213.91c40.546 40.307 106.834 23.713 121.952 8.648 0 0-63.75 36.047-112.283-15.137-28.025-29.555-20.351-74.382-19.084-80.759l.008.002 21.206 4.201a.79.79 0 0 0 .862-1.123L85.253 85.94a.786.786 0 0 0-1.23-.244l-40.65 31.902a.793.793 0 0 0 .369 1.368l21.032 4.166c-1.417 6.529-10.048 53.709 27.243 90.778m31.837-28.556v14.684c0 .546.442.988.987.988h71.239a.986.986 0 0 0 .986-.988v-18.316a.985.985 0 0 0-.986-.986h-44.931l43.439-81.244a.981.981 0 0 0 .117-.465V82.174a.987.987 0 0 0-.987-.987h-65.309a.987.987 0 0 0-.987.987v18.469c0 .546.442.987.987.987h40.7l-45.136 83.253a1 1 0 0 0-.119.471"></path>
              </svg>
            </span>
          )}
        </a>
        <div className="flex flex-row gap-x-2 text-xs laptop:text-sm">
          <span className="text-gray-400">{t('ProductCode')}</span>
          <span className="font-medium">{props.productCode}</span>
        </div>
        {props.brand && (
          <div className="flex flex-row gap-x-2 text-xs laptop:text-sm">
            <span className="text-gray-400">{t('Brand')}</span>
            <span className="font-medium">{props.brand}</span>
          </div>
        )}
      </div>

      <div className="availability">
        <div className="text-xs laptop:text-base text-stock font-medium">{props.availability}</div>
      </div>

      <div className="unit-count">
        <input
          type="number"
          inputMode="numeric"
          name="unitCount"
          min={1}
          defaultValue={props.unitCount}
          onChange={e => {
            const count = parseInt(e.currentTarget.value);
            setUnitCount(count);
          }}
          onBlur={() => {
            if (isNaN(unitCount) || unitCount === props.unitCount) return;
            onUpdate(unitCount);
          }}
          className="as-text w-16 h-8 px-1 text-center text-xs laptop:text-sm"
        />
      </div>

      <div className="price">
        <span className="text-price font-bold">{props.linePrice.formatted}</span>
        <span className="text-xs laptop:text-sm text-gray-400 font-medium">
          {props.unitCount}x {props.unitPrice.formatted}
        </span>
        {props.lineSaving && props.lineSaving.value > 0 && (
          <div className="text-xs laptop:text-sm text-price-discount">
            <span className="mr-1">{t('YourSaving')}</span>
            <span className="font-medium">{props.lineSaving.formatted}</span>
          </div>
        )}
      </div>

      <div className="delete">
        <button
          className="w-6 h-6 inline-flex items-center justify-center bg-gray-200 rounded-full text-gray-600 text-xxs"
          onClick={() => onUpdate(0)}
          type="button">
          <i className="fa-solid fa-times" />
        </button>
      </div>
    </div>
  );
}
